import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { global } from "@shared/libs/Global";
import { callApi } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/**
 * 프로젝트 공유폴더 — WBS 진행등록 팝업 하단 (ISS2026100053)
 * 왼쪽 폴더 트리 + 오른쪽 선택 폴더 내용. sys_attachfile 의 id_p / yn_directory / nm_directory 로 만든 논리 트리.
 *   - 처음 열 때 폴더가 없으면 WBS 대표ACT 이름으로 최상위 폴더 자동 생성
 *   - PC 파일을 끌어다 놓으면 그 폴더로 업로드, 목록 안 항목을 폴더로 끌어다 놓으면 이동
 *   - 폴더는 MAX_DEPTH 단계까지(백엔드도 같은 값으로 검사)
 *   - 실제 파일은 프로젝트 단위 한 경로에 저장되므로 파일명은 프로젝트 안에서 중복 불가
 */
const CD_MENU = "SN_SFA_PJT_REG_002";
const CD_MODULE = "SFA";
const MAX_DEPTH = 3; // 백엔드 sn_sfa_pjt_reg_002_attach_service.MAX_DEPTH 와 같게
const ROOT = -1; // 최상위 위치(폴더 아님)
const DRAG_TYPE = "application/x-snotes-attach-id";

type Item = {
  id: number;
  id_p: number | null;
  yn_directory: string;
  nm_directory?: string;
  nm_file?: string;
  qt_size?: number;
  tm_reg?: string;
  cd_user_reg?: string;
  tm_last?: string | number; // 수정한 날짜 (수정일시 없으면 등록일시)
  nm_user_last?: string; // 최종 수정자 (없으면 등록자)
};

interface Props {
  cdPjt: string;
  rootNames: string[]; // WBS 대표ACT — 폴더 자동 생성용
}

// 툴바 버튼: 아이콘 + 명칭 (테두리 없음)
const ToolBtn: React.FC<{ icon: React.ReactNode; label: string; disabled?: boolean; onClick: () => void }> = ({
  icon,
  label,
  disabled,
  onClick,
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="flex items-center gap-1 px-2 py-[2px] rounded text-[#333] hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
  >
    <span className="flex text-[15px]">{icon}</span>
    {label}
  </button>
);

const isFolder = (i: Item) => i.yn_directory === "Y";
const nameOf = (i: Item) => (isFolder(i) ? i.nm_directory : i.nm_file) || "";
// 서버 일시(epoch ms 또는 문자열) → YYYY/MM/DD
const fmtDate = (v?: string | number) => {
  if (v === undefined || v === null || v === "") return "";
  const d = new Date(typeof v === "number" ? v : String(v).replace(" ", "T"));
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
};
const fmtSize = (n?: number) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
};

const ProjectFolder: React.FC<Props> = ({ cdPjt, rootNames }) => {
  const { userInfo } = global();
  const [items, setItems] = useState<Item[]>([]);
  const [current, setCurrent] = useState<number>(ROOT); // 선택 폴더
  const [selected, setSelected] = useState<number | null>(null); // 오른쪽 목록에서 선택한 항목
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [edit, setEdit] = useState<{ mode: "new" | "rename"; value: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initTried = useRef<string>("");

  const base = { cd_firm: userInfo.cd_firm, cd_pjt: cdPjt };

  // ── 데이터 ──
  const apply = (res: any) => {
    if (res?.status && Array.isArray(res.data)) setItems(res.data);
    return !!res?.status;
  };

  useEffect(() => {
    setItems([]);
    setCurrent(ROOT);
    setSelected(null);
    if (!cdPjt || cdPjt.length < 3) return;
    (async () => {
      const res = await callApi("SN_SFA_PJT_REG_002_ATTACH_TREE_01", base);
      apply(res);
      const hasFolder = Array.isArray(res?.data) && res.data.some(isFolder);
      if (!hasFolder && rootNames.length && initTried.current !== cdPjt) {
        initTried.current = cdPjt;
        apply(await callApi("SN_SFA_PJT_REG_002_ATTACH_INIT_01", { ...base, names: rootNames }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cdPjt, rootNames.join("|")]);

  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const childrenOf = (p: number) =>
    items
      .filter((i) => (i.id_p ?? ROOT) === p)
      .sort((a, b) => Number(isFolder(b)) - Number(isFolder(a)) || nameOf(a).localeCompare(nameOf(b), "ko"));
  const depthOf = (id: number) => {
    let d = 0;
    for (let p: number | null = id; p !== null && p !== ROOT && byId.has(p); p = byId.get(p)!.id_p) d++;
    return d;
  };
  // 이 폴더를 포함한 하위 폴더 단계 수
  const heightOf = (id: number): number =>
    1 + Math.max(0, ...items.filter((i) => isFolder(i) && i.id_p === id).map((i) => heightOf(i.id)));
  const hasSiblingName = (parent: number, name: string, selfId?: number) =>
    items.some((i) => isFolder(i) && (i.id_p ?? ROOT) === parent && i.id !== selfId && i.nm_directory === name);
  const pathOf = (id: number) => {
    const names: string[] = [];
    for (let p: number | null = id; p !== null && p !== ROOT && byId.has(p); p = byId.get(p)!.id_p)
      names.unshift(nameOf(byId.get(p)!));
    return names;
  };

  // ── 폴더 만들기 / 이름 바꾸기 / 삭제 ──
  const startNew = async () => {
    if (current !== ROOT && depthOf(current) + 1 > MAX_DEPTH) {
      await showDialog("alter", `폴더는 ${MAX_DEPTH}단계까지 만들 수 있습니다.`);
      return;
    }
    setEdit({ mode: "new", value: "" });
  };
  const startRename = () => {
    if (current === ROOT) return;
    setEdit({ mode: "rename", value: nameOf(byId.get(current)!) });
  };
  const commitEdit = async () => {
    if (!edit) return;
    const name = edit.value.trim();
    if (!name) {
      await showDialog("alter", "폴더명을 입력해 주시기 바랍니다.");
      return;
    }
    const parent = edit.mode === "rename" ? byId.get(current)?.id_p ?? ROOT : current;
    if (hasSiblingName(parent, name, edit.mode === "rename" ? current : undefined)) {
      await showDialog("alter", "같은 위치에 같은 이름의 폴더가 있습니다.");
      return;
    }
    const ok = apply(
      await callApi("SN_SFA_PJT_REG_002_ATTACH_FOLDER_SAVE_01", {
        ...base,
        nm_directory: name,
        ...(edit.mode === "rename"
          ? { id: current }
          : { id_p: current === ROOT ? null : current }),
      })
    );
    if (ok) {
      if (edit.mode === "new" && current !== ROOT) setExpanded((s) => new Set(s).add(current));
      setEdit(null);
    }
  };

  const onDelete = async () => {
    const target = selected !== null ? byId.get(selected) : current !== ROOT ? byId.get(current) : undefined;
    if (!target) return;
    if (isFolder(target)) {
      const ok = await showDialog("confirm", `[${nameOf(target)}] 폴더와 안의 모든 파일을 삭제하시겠습니까?`);
      if (!ok) return;
      const done = apply(await callApi("SN_SFA_PJT_REG_002_ATTACH_FOLDER_DELETE_01", { ...base, id: target.id }));
      if (done) {
        if (current === target.id) setCurrent(target.id_p ?? ROOT);
        setSelected(null);
      }
      return;
    }
    const ok = await showDialog("confirm", `[${nameOf(target)}] 파일을 삭제하시겠습니까?`);
    if (!ok) return;
    try {
      await axios.delete(`/snotes/file/delete/${CD_MODULE}/${target.id}`, {
        withCredentials: true,
        headers: { "X-TENANT-ID": userInfo.tenant_id || "DEFAULT" },
      });
      setSelected(null);
      apply(await callApi("SN_SFA_PJT_REG_002_ATTACH_TREE_01", base));
    } catch (e) {
      await showDialog("error", "파일 삭제를 실패했습니다.");
    }
  };

  // ── 업로드 / 다운로드 ──
  const upload = async (files: File[], folderId: number) => {
    if (!files.length) return;
    // 실제 저장 경로가 프로젝트 단위 하나라 같은 이름이면 다른 폴더 파일을 덮어쓴다 → 막는다
    const names = new Set(items.filter((i) => !isFolder(i)).map((i) => i.nm_file));
    const dup = files.find((f) => names.has(f.name));
    if (dup) {
      await showDialog("alter", `[${dup.name}] 같은 이름의 파일이 이미 있습니다.\n파일명을 바꿔서 올려주시기 바랍니다.`);
      return;
    }
    const tooLong = files.find((f) => f.name.length > 200);
    if (tooLong) {
      await showDialog("alter", `파일명은 200자까지 등록하실 수 있습니다.\n(현재 ${tooLong.name.length}자: ${tooLong.name})`);
      return;
    }
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("cdFirm", userInfo.cd_firm || "");
    fd.append("cdMenu", CD_MENU);
    fd.append("noDocu", cdPjt);
    fd.append("noLineDocu", "0");
    fd.append("dtDocu", "");
    fd.append("cdUser", userInfo.cd_user || "");
    fd.append("cdModule", CD_MODULE);
    if (folderId !== ROOT) fd.append("idP", String(folderId));
    try {
      await axios.post("/snotes/file/upload", fd, {
        headers: { "Content-Type": "multipart/form-data", "X-TENANT-ID": userInfo.tenant_id || "DEFAULT" },
      });
      apply(await callApi("SN_SFA_PJT_REG_002_ATTACH_TREE_01", base));
    } catch (e) {
      await showDialog("error", "파일 업로드를 실패했습니다.");
    }
  };

  const download = async (item: Item) => {
    try {
      const res = await axios.post(
        "/snotes/file/download",
        { id: item.id, cdModule: CD_MODULE },
        { headers: { "X-TENANT-ID": userInfo.tenant_id || "DEFAULT" }, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", item.nm_file || "");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      await showDialog("error", "파일 다운로드를 실패했습니다.");
    }
  };

  // ── 드래그앤드롭: PC 파일 = 업로드, 목록 항목 = 이동 ──
  const onDragStart = (e: React.DragEvent, id: number) => {
    e.dataTransfer.setData(DRAG_TYPE, String(id));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e: React.DragEvent, folderId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(folderId);
  };
  const onDrop = async (e: React.DragEvent, folderId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    const moveId = e.dataTransfer.getData(DRAG_TYPE);
    if (moveId) {
      const id = Number(moveId);
      const item = byId.get(id);
      if (!item || id === folderId || (item.id_p ?? ROOT) === folderId) return;
      if (isFolder(item)) {
        for (let p: number | null = folderId; p !== null && p !== ROOT && byId.has(p); p = byId.get(p)!.id_p) {
          if (p === id) {
            await showDialog("alter", "폴더를 자기 하위로 옮길 수 없습니다.");
            return;
          }
        }
        if ((folderId === ROOT ? 0 : depthOf(folderId)) + heightOf(id) > MAX_DEPTH) {
          await showDialog("alter", `이동하면 폴더가 ${MAX_DEPTH}단계를 넘습니다.`);
          return;
        }
        if (hasSiblingName(folderId, item.nm_directory || "", id)) {
          await showDialog("alter", "옮길 위치에 같은 이름의 폴더가 있습니다.");
          return;
        }
      }
      apply(
        await callApi("SN_SFA_PJT_REG_002_ATTACH_MOVE_01", {
          ...base,
          id,
          id_p: folderId === ROOT ? null : folderId,
        })
      );
      return;
    }
    if (e.dataTransfer.files?.length) await upload(Array.from(e.dataTransfer.files), folderId);
  };

  // ── 렌더 ──
  const toggle = (id: number) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const renderTree = (parent: number, depth: number): React.ReactNode =>
    childrenOf(parent)
      .filter(isFolder)
      .map((f) => {
        const kids = childrenOf(f.id).filter(isFolder);
        const open = expanded.has(f.id);
        return (
          <div key={f.id}>
            <div
              draggable
              onDragStart={(e) => onDragStart(e, f.id)}
              onDragOver={(e) => onDragOver(e, f.id)}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) => onDrop(e, f.id)}
              onClick={() => {
                setCurrent(f.id);
                setSelected(null);
              }}
              className="flex items-center gap-1 cursor-pointer rounded px-1 py-[2px] text-xs whitespace-nowrap"
              style={{
                paddingLeft: 4 + depth * 14,
                background: dropTarget === f.id ? "#dbeafe" : current === f.id ? "#eef4ff" : undefined,
                color: current === f.id ? "#1d4ed8" : undefined,
              }}
            >
              <span
                className="w-3 text-gray-400"
                onClick={(e) => {
                  e.stopPropagation();
                  if (kids.length) toggle(f.id);
                }}
              >
                {kids.length ? (open ? "▾" : "▸") : ""}
              </span>
              <span>📁</span>
              <span>{f.nm_directory}</span>
            </div>
            {open && renderTree(f.id, depth + 1)}
          </div>
        );
      });

  const list = childrenOf(current);
  const disabled = !cdPjt || cdPjt.length < 3;

  return (
    <div className="h-full flex flex-col border border-[#babfc7] bg-white">
      {/* 툴바 */}
      <div className="flex items-center gap-1 px-2 py-1 border-b text-xs shrink-0">
        <ToolBtn icon={<CreateNewFolderOutlinedIcon fontSize="inherit" />} label="새 폴더" disabled={disabled} onClick={startNew} />
        <ToolBtn icon={<FileUploadOutlinedIcon fontSize="inherit" />} label="업로드" disabled={disabled} onClick={() => fileInputRef.current?.click()} />
        <ToolBtn icon={<DriveFileRenameOutlineIcon fontSize="inherit" />} label="이름 변경" disabled={disabled || current === ROOT} onClick={startRename} />
        <ToolBtn icon={<DeleteOutlineIcon fontSize="inherit" />} label="삭제" disabled={disabled || (selected === null && current === ROOT)} onClick={onDelete} />
        {edit && (
          <span className="flex items-center gap-1 ml-2">
            <input
              autoFocus
              value={edit.value}
              maxLength={100}
              placeholder={edit.mode === "new" ? "새 폴더명" : "폴더명"}
              onChange={(e) => setEdit({ ...edit, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitEdit();
                if (e.key === "Escape") setEdit(null);
              }}
              className="border rounded px-1 py-[1px] w-40"
            />
            <button type="button" onClick={commitEdit} className="px-2 py-[2px] rounded border text-blue-600">확인</button>
            <button type="button" onClick={() => setEdit(null)} className="px-2 py-[2px] rounded border">취소</button>
          </span>
        )}
        <span className="ml-auto text-gray-400">파일을 끌어다 놓으면 선택한 폴더로 올라갑니다 · 폴더 {MAX_DEPTH}단계까지</span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const fs = Array.from(e.target.files || []);
            e.target.value = "";
            upload(fs, current);
          }}
        />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* 폴더 트리 */}
        <div className="w-56 shrink-0 overflow-auto border-r py-1">
          <div
            onDragOver={(e) => onDragOver(e, ROOT)}
            onDragLeave={() => setDropTarget(null)}
            onDrop={(e) => onDrop(e, ROOT)}
            onClick={() => {
              setCurrent(ROOT);
              setSelected(null);
            }}
            className="flex items-center gap-1 cursor-pointer rounded px-1 py-[2px] text-xs font-semibold"
            style={{ background: dropTarget === ROOT ? "#dbeafe" : current === ROOT ? "#eef4ff" : undefined }}
          >
            <span>🗂</span>
            <span>공유폴더</span>
          </div>
          {renderTree(ROOT, 1)}
        </div>

        {/* 선택 폴더 내용 */}
        <div
          className="flex-1 min-w-0 overflow-auto"
          onDragOver={(e) => onDragOver(e, current)}
          onDragLeave={() => setDropTarget(null)}
          onDrop={(e) => onDrop(e, current)}
          style={{ background: dropTarget === current ? "#f5f9ff" : undefined }}
        >
          <div className="px-2 py-1 text-xs text-gray-500 border-b">
            공유폴더{pathOf(current).map((n) => ` › ${n}`).join("")}
          </div>
          {list.length === 0 ? (
            <div className="text-xs text-gray-400 text-center py-6">
              {disabled ? "프로젝트를 선택해 주시기 바랍니다." : "파일을 마우스로 끌어 오세요"}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#64748b] border-b">
                  <th className="px-2 py-1 text-left font-normal">이름</th>
                  <th className="px-2 py-1 text-left font-normal w-28">수정한 날짜</th>
                  <th className="px-2 py-1 text-left font-normal w-24">최종 수정자</th>
                  <th className="px-2 py-1 text-right font-normal w-24">파일 크기</th>
                </tr>
              </thead>
              <tbody>
                {current !== ROOT && (
                  <tr
                    className="cursor-pointer hover:bg-gray-50"
                    onDoubleClick={() => setCurrent(byId.get(current)?.id_p ?? ROOT)}
                    onDragOver={(e) => onDragOver(e, byId.get(current)?.id_p ?? ROOT)}
                    onDrop={(e) => onDrop(e, byId.get(current)?.id_p ?? ROOT)}
                  >
                    <td className="px-2 py-1" colSpan={4}>⤴ ..</td>
                  </tr>
                )}
                {list.map((i) => (
                  <tr
                    key={i.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, i.id)}
                    onDragOver={isFolder(i) ? (e) => onDragOver(e, i.id) : undefined}
                    onDrop={isFolder(i) ? (e) => onDrop(e, i.id) : undefined}
                    onClick={() => setSelected(i.id)}
                    onDoubleClick={() => {
                      if (isFolder(i)) {
                        setCurrent(i.id);
                        setSelected(null);
                        setExpanded((s) => new Set(s).add(i.id_p ?? ROOT));
                      } else download(i);
                    }}
                    className="cursor-pointer border-b border-gray-100"
                    style={{ background: dropTarget === i.id && isFolder(i) ? "#dbeafe" : selected === i.id ? "#eef4ff" : undefined }}
                    title={isFolder(i) ? "더블클릭: 열기" : "더블클릭: 다운로드"}
                  >
                    <td className="px-2 py-1">
                      {isFolder(i) ? "📁 " : "📄 "}
                      {nameOf(i)}
                    </td>
                    <td className="px-2 py-1 text-gray-500">{fmtDate(i.tm_last)}</td>
                    <td className="px-2 py-1 text-gray-500">{i.nm_user_last || ""}</td>
                    <td className="px-2 py-1 text-right text-gray-500">{isFolder(i) ? "" : fmtSize(i.qt_size)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectFolder;
